/**
 * GameHud.jsx
 * 
 * Phase 3: HUD component for displaying stocks, damage, and timers
 * React-based overlay that updates with game state
 */

import React from 'react';

const GAME_WIDTH = 1200;
const GAME_HEIGHT = 600;

// Stock icon component
const StockIcon = ({ filled, color }) => (
  <svg
    width="16"
    height="20"
    viewBox="0 0 20 24"
    style={{
      marginRight: '2px',
      opacity: filled ? 1 : 0.2,
      transition: 'opacity 0.2s ease-out',
    }}
  >
    <path
      d="M10 2 L18 8 L18 18 L10 24 L2 18 L2 8 Z"
      fill={filled ? color : '#666'}
      stroke={filled ? color : '#444'}
      strokeWidth="1"
    />
  </svg>
);

// Damage bar component
const DamageBar = ({ damage, maxDamage = 150 }) => {
  const percentage = Math.min((damage / maxDamage) * 100, 100);
  
  let color = '#22c55e';
  if (damage > 50) color = '#eab308';
  if (damage > 100) color = '#f97316';
  if (damage > 130) color = '#ef4444';

  return (
    <div
      style={{
        width: '60px',
        height: '4px',
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: '2px',
        overflow: 'hidden',
        marginTop: '2px',
      }}
    >
      <div
        style={{
          width: `${percentage}%`,
          height: '100%',
          backgroundColor: color,
          transition: 'width 0.1s ease-out',
        }}
      />
    </div>
  );
};

// Format seconds to MM:SS
const formatTime = (seconds) => {
  if (seconds === undefined || seconds === null) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const GameHud = ({
  gameState,
  leftCharacter,
  rightCharacter,
  gameTime,
  isLocalPlayerLeft = true,
}) => {
  if (!gameState) return null;

  const left = gameState.left;
  const right = gameState.right;
  
  const leftDamage = Math.floor(left?.hp || 0);
  const rightDamage = Math.floor(right?.hp || 0);
  const leftStocks = left?.stocks || 0;
  const rightStocks = right?.stocks || 0;

  return (
    <div
      className="game-hud"
      style={{
        position: 'absolute',
        width: GAME_WIDTH,
        height: GAME_HEIGHT,
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 10,
        overflow: 'hidden',
      }}
    >
      {/* Timer - centered at top */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '12px',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          zIndex: 20,
        }}
      >
        <div
          style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '2px',
          }}
        >
          {formatTime(gameTime)}
        </div>
      </div>

      {/* Left player HUD */}
      <div
        style={{
          position: 'absolute',
          left: '12px',
          top: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {/* P1 Label */}
        <div
          style={{
            fontSize: '12px',
            fontWeight: '700',
            color: isLocalPlayerLeft ? '#4ade80' : '#f87171',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          }}
        >
          P1
        </div>
        
        {/* Character name */}
        <div
          style={{
            fontSize: '10px',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '4px',
            maxWidth: '100px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {leftCharacter?.name || 'Unknown'}
        </div>

        {/* Stocks */}
        <div style={{ display: 'flex', marginBottom: '4px' }}>
          {[0, 1, 2].map((i) => (
            <StockIcon
              key={i}
              filled={i < leftStocks}
              color={isLocalPlayerLeft ? '#4ade80' : '#f87171'}
            />
          ))}
        </div>

        {/* Damage */}
        <div
          style={{
            fontSize: '24px',
            fontWeight: '800',
            color: leftDamage > 100 ? '#ef4444' : '#ffffff',
            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            lineHeight: 1,
          }}
        >
          {leftDamage}%
        </div>
        
        <DamageBar damage={leftDamage} />
      </div>

      {/* Right player HUD */}
      <div
        style={{
          position: 'absolute',
          right: '12px',
          top: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        {/* P2 Label */}
        <div
          style={{
            fontSize: '12px',
            fontWeight: '700',
            color: !isLocalPlayerLeft ? '#4ade80' : '#f87171',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 1px 2px rgba(0,0,0,0.8)',
          }}
        >
          P2
        </div>
        
        {/* Character name */}
        <div
          style={{
            fontSize: '10px',
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '4px',
            maxWidth: '100px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {rightCharacter?.name || 'Unknown'}
        </div>

        {/* Stocks */}
        <div style={{ display: 'flex', flexDirection: 'row-reverse', marginBottom: '4px' }}>
          {[0, 1, 2].map((i) => (
            <StockIcon
              key={i}
              filled={i < rightStocks}
              color={!isLocalPlayerLeft ? '#4ade80' : '#f87171'}
            />
          ))}
        </div>

        {/* Damage */}
        <div
          style={{
            fontSize: '24px',
            fontWeight: '800',
            color: rightDamage > 100 ? '#ef4444' : '#ffffff',
            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            lineHeight: 1,
          }}
        >
          {rightDamage}%
        </div>
        
        <DamageBar damage={rightDamage} />
      </div>
    </div>
  );
};

export default GameHud;
